/**
 * Триаж парка: что из двадцати устройств требует человека прямо сейчас.
 *
 * Идея из практики флот-операций: нормальные состояния подавляются, наверх
 * всплывают отклонения. Если оператор видит двадцать строк и не может назвать
 * те три, где нужно вмешаться, экран производит шум, а не информацию.
 *
 * Правила намеренно узкие. Дешевле пропустить спорный случай, чем приучить
 * оператора игнорировать раздел «требуют внимания».
 */

import { useMemo } from 'react';
import { ModemData } from '../api/Drone';
import { HubTelemetryMessage } from '../asp/DroneHub';
import { isReturningOrLanding } from '../utils/mavMode';

export type AttentionLevel = 'critical' | 'warning';

/** Ключ в public/locales/<lng>/console.json. */
export type AttentionReason =
    | 'attention_offline'
    | 'attention_battery_critical'
    | 'attention_battery_low'
    | 'attention_no_gps'
    | 'attention_returning'
    | 'attention_firmware';

export interface AttentionItem {
    imei: string;
    name: string;
    level: AttentionLevel;
    reason: AttentionReason;
    /** Подставляется в перевод: проценты, число спутников. */
    value?: number;
}

// Пороги. Батарея: 15% — уже поздно, 25% — пора планировать возврат.
const BATTERY_CRITICAL = 15;
const BATTERY_LOW = 25;
// Меньше шести спутников — позиция недостоверна.
const MIN_SATELLITES = 6;

/**
 * Заряд самой разряженной банки.
 *
 * Проверка на массив обязательна, а не «на всякий случай»: сервер присылает в
 * batteryRemaining не только массив, и `??` от этого не спасает — он ловит лишь
 * null и undefined. Без Array.isArray здесь падал .map, а вместе с ним всё
 * дерево React: error boundary в приложении нет.
 */
function lowestBattery(telemetry: HubTelemetryMessage | undefined, drone: ModemData): number | null {
    const raw = telemetry?.batteryRemaining ?? drone.batteryRemaining;
    if (!Array.isArray(raw)) {
        return null;
    }
    const values = raw
        .map((b) => b?.remaining)
        .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0);

    return values.length > 0 ? Math.min(...values) : null;
}

/**
 * Один дрон — не более одной причины: самой severe. Иначе аппарат с севшей
 * батареей занял бы три строки и вытеснил остальных.
 */
function classify(drone: ModemData, telemetry: HubTelemetryMessage | undefined): AttentionItem | null {
    const base = { imei: drone.imei, name: drone.name };

    // Офлайн интересен только у того, кто был в воздухе: спокойно лежащий на
    // полке аппарат — не происшествие.
    if (!drone.isOnline) {
        return drone.isArm
            ? { ...base, level: 'critical', reason: 'attention_offline' }
            : null;
    }

    const battery = lowestBattery(telemetry, drone);
    if (battery !== null && battery <= BATTERY_CRITICAL) {
        return { ...base, level: 'critical', reason: 'attention_battery_critical', value: Math.round(battery) };
    }

    if (drone.isArm && telemetry && telemetry.sputniksCount < MIN_SATELLITES) {
        return { ...base, level: 'critical', reason: 'attention_no_gps', value: telemetry.sputniksCount };
    }

    if (battery !== null && battery <= BATTERY_LOW) {
        return { ...base, level: 'warning', reason: 'attention_battery_low', value: Math.round(battery) };
    }

    if (drone.isArm && isReturningOrLanding(drone.mavType, drone.mode)) {
        return { ...base, level: 'warning', reason: 'attention_returning' };
    }

    // Прошивка — не срочность, а гигиена: только для тех, кто не в воздухе,
    // иначе оператор увидит её посреди полёта и ничего не сможет сделать.
    if (!drone.isArm && drone.version >= 2000 && (drone.upgradable || drone.isLastFirmware !== 0)) {
        return { ...base, level: 'warning', reason: 'attention_firmware' };
    }

    return null;
}

const LEVEL_ORDER: Record<AttentionLevel, number> = { critical: 0, warning: 1 };

/** Устройства, требующие вмешательства: критичные выше, внутри — по имени. */
function useDroneTriage(
    drones: ModemData[],
    telemetry: Map<string, HubTelemetryMessage>
): AttentionItem[] {
    return useMemo(() => {
        const items: AttentionItem[] = [];
        drones.forEach((drone) => {
            const item = classify(drone, telemetry.get(drone.imei));
            if (item) {
                items.push(item);
            }
        });

        return items.sort((a, b) =>
            LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]
            || a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [drones, telemetry]);
}

export default useDroneTriage;
